import { useCallback, useEffect, useMemo, useState } from 'react';
import { Option, OptionKey, SpendPeriodType } from '@common-fe/common-fe';

import { NodeOption } from '@/common/types';

import { useGetSpendLimitPeriod } from '../../SpendPeriodTracker/hooks';

interface UseSpendLimitsPeriodProps {
  accountId?: string;
}

interface UseSpendLimitsPeriodResult {
  showPeriodDropdown: boolean;
  periodOptions: Option[];
  selectedPeriod: OptionKey;
  handlePeriodChange: (value: OptionKey) => void;
  isPeriodLoading: boolean;
  periodFrom?: string;
}

const useSpendLimitsPeriod = ({ accountId }: UseSpendLimitsPeriodProps): UseSpendLimitsPeriodResult => {
  const [selectedPeriod, setSelectedPeriod] = useState<OptionKey>('');
  const {
    formattedData: periodData,
    isLoading: isPeriodLoading,
    planYearOptions,
    monthOptions,
    defaultPeriod,
  } = useGetSpendLimitPeriod(accountId);

  const showPeriodDropdown = useMemo(
    () =>
      periodData?.spendPeriodType === SpendPeriodType.PLAN_YEAR ||
      periodData?.spendPeriodType === SpendPeriodType.MONTHLY,
    [periodData?.spendPeriodType]
  );

  const periodOptions = useMemo<NodeOption[]>(() => {
    if (periodData?.spendPeriodType === SpendPeriodType.PLAN_YEAR) {
      return planYearOptions as NodeOption[];
    }
    if (periodData?.spendPeriodType === SpendPeriodType.MONTHLY) {
      return monthOptions as NodeOption[];
    }
    return [];
  }, [periodData?.spendPeriodType, planYearOptions, monthOptions]);

  useEffect(() => {
    if (defaultPeriod) {
      setSelectedPeriod(defaultPeriod);
    }
  }, [defaultPeriod]);

  const handlePeriodChange = useCallback((value: OptionKey) => {
    setSelectedPeriod(value);
  }, []);

  return {
    showPeriodDropdown,
    periodOptions,
    selectedPeriod,
    handlePeriodChange,
    isPeriodLoading,
  };
};

export default useSpendLimitsPeriod;
